import type { KycTierRow, KycTierStatus } from '@contract';
import { formatMoney } from '@sao/shared/money';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table';
import { PageHeader } from '@/components/page-header';
import { StatusPill } from '@/components/primitives/status-pill';
import { ProposeChange, type ProposeField } from '@/components/propose-change';
import { TierLimitsEmpty } from '@/components/tier-limits-empty';
import { Alert } from '@/components/ui/alert';
import { formatDate } from '@/lib/format';
import { adminQuery } from '@/lib/query';
import type { Tone } from '@/lib/status';

export const Route = createFileRoute('/_app/tiers')({
  component: TiersPage,
});

const STATUS_WORDS: Record<KycTierStatus, { label: string; tone: Tone }> = {
  open: { label: 'Open', tone: 'success' },
  sandbox: { label: 'Sandbox', tone: 'warning' },
  proposed: { label: 'Proposed', tone: 'neutral' },
  blocked: { label: 'Blocked', tone: 'danger' },
};

const LIMITS = [
  { name: 'max_balance_minor', label: 'Highest balance' },
  { name: 'per_transaction_limit_minor', label: 'Per transaction' },
  { name: 'daily_limit_minor', label: 'Per day' },
  { name: 'monthly_limit_minor', label: 'Per month' },
] as const;

const column = createColumnHelper<KycTierRow>();

function money(row: KycTierRow, value: number | null): string {
  return value == null ? '—' : formatMoney(value, row.limit_currency);
}

const columns = [
  column.accessor('tier', {
    header: 'Tier',
    cell: ({ row }) => (
      <span className="font-medium">
        {row.original.tier} · {row.original.name}
      </span>
    ),
  }),
  column.accessor('status', {
    header: 'Status',
    cell: ({ row }) => {
      const words = STATUS_WORDS[row.original.status];
      return (
        <span className="flex flex-col gap-1">
          <StatusPill tone={words.tone} label={words.label} />
          {row.original.in_force ? null : (
            <span className="text-xs text-muted-foreground">Not in force</span>
          )}
        </span>
      );
    },
  }),
  column.accessor('valid_until', {
    header: 'Valid until',
    cell: (info) => (info.getValue() ? formatDate(info.getValue()) : '—'),
  }),
  ...LIMITS.map((limit) =>
    column.display({
      id: limit.name,
      header: limit.label,
      cell: ({ row }) => (
        <span className="whitespace-nowrap tabular-nums">
          {money(row.original, row.original[limit.name])}
        </span>
      ),
    }),
  ),
  column.accessor('customers', { header: 'Customers' }),
  column.display({
    id: 'change',
    header: 'Change',
    cell: ({ row }) => <TierChange tier={row.original} />,
  }),
];

/** Fields for one tier; blank limits stay as they are. */
function fieldsFor(tier: KycTierRow): ProposeField[] {
  const statuses = Object.keys(STATUS_WORDS) as KycTierStatus[];
  return [
    {
      name: 'status',
      label: 'Status',
      kind: 'select',
      options: [
        tier.status,
        ...statuses.filter((value) => value !== tier.status),
      ].map((value) => ({ value, label: STATUS_WORDS[value].label })),
      hint: 'Open applies the limits. Sandbox applies them until the date below. Proposed keeps the figures for review and counts as none. Blocked allows nothing.',
    },
    {
      name: 'valid_until',
      label: 'Valid until (a date, for sandbox)',
      kind: 'text',
      optional: true,
      hint: 'Leave blank to keep the date as it is.',
    },
    ...LIMITS.map(
      (limit): ProposeField => ({
        name: limit.name,
        label: `${limit.label} (${tier.limit_currency})`,
        kind: 'amount',
        currency: tier.limit_currency,
        optional: true,
        asNumber: true,
        clearable: true,
        hint: `Now ${tier[limit.name] == null ? 'no limit' : money(tier, tier[limit.name])}. Leave blank to keep it, or type none for no limit.`,
      }),
    ),
  ];
}

function TierChange({ tier }: Readonly<{ tier: KycTierRow }>) {
  if (tier.change_request_id) {
    return (
      <Link
        to="/approvals/$requestId"
        params={{ requestId: tier.change_request_id }}
        className="text-sm underline"
      >
        Waiting for approval
      </Link>
    );
  }
  return (
    <ProposeChange
      kind="kyc_tier"
      payload={{ market: tier.market, tier: tier.tier }}
      into="values"
      label="Change"
      title={`Change tier ${tier.tier}, ${tier.name}`}
      description="Changes this tier's status, expiry or limits for every customer at it."
      fields={fieldsFor(tier)}
      changes={[
        {
          label: 'Now',
          before: `${STATUS_WORDS[tier.status].label}; per day ${money(tier, tier.daily_limit_minor)}; per month ${money(tier, tier.monthly_limit_minor)}`,
          after: 'As you fill in below.',
        },
      ]}
    />
  );
}

/** The verification tiers of this market and what each lets a customer do. */
function TiersPage() {
  const list = useQuery(adminQuery('kyc_tiers.list', {}));
  return (
    <>
      <PageHeader
        title="Tier limits"
        description="What each verification tier lets a customer hold and move in this market. Only open tiers, and sandbox tiers before their date, apply. A change is sent for approval."
      />
      {list.isError ? (
        <Alert variant="destructive">{list.error.message}</Alert>
      ) : null}
      {list.data && list.data.rows.length === 0 ? <TierLimitsEmpty /> : null}
      {list.data && list.data.rows.length > 0 ? (
        <DataTable id="kyc-tiers" columns={columns} data={list.data.rows} />
      ) : null}
    </>
  );
}
